import { lazy, Suspense, type ComponentType } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { EditorPage } from './routes/EditorPage';
import { SharePage } from './routes/SharePage';
import { SitePage } from './routes/SitePage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useTheme } from './hooks/useTheme';
import { useNativeLinks } from './hooks/useNativeLinks';

// Build-time dev-only: the `import()` lives inside a branch that Vite replaces
// with `false` in production, so Rollup dead-code-eliminates it — the styleguide
// chunk and its assets are never emitted or precached in prod.
const StyleguidePage: ComponentType | null = import.meta.env.DEV
  ? lazy(() => import('./routes/StyleguidePage'))
  : null;

// Operators only, and rarely: its own chunk, so no visitor downloads it.
const AdminReportsPage = lazy(() => import('./routes/AdminReportsPage'));

export function App() {
  useTheme();
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <NativeLinks />
        <Routes>
          <Route path="/" element={<EditorPage />} />
          <Route path="/s/:token" element={<SharePage />} />
          {/* A published address is either one note or a site. The share
              viewer asks first and hands over when the slug is a site's. */}
          <Route path="/p/:slug" element={<SharePage />} />
          <Route path="/p/:slug/:page" element={<SitePage />} />
          <Route
            path="/admin/reports"
            element={
              <Suspense fallback={null}>
                <AdminReportsPage />
              </Suspense>
            }
          />
          {StyleguidePage && (
            <Route
              path="/styleguide"
              element={
                <Suspense fallback={null}>
                  <StyleguidePage />
                </Suspense>
              }
            />
          )}
          {/* Catch-all: unknown paths (and direct /index.html loads) open the editor. */}
          <Route path="*" element={<EditorPage />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

/**
 * Routes links the system hands the running app. Inside the router, because
 * that is the only place with a `navigate`; renders nothing.
 */
function NativeLinks() {
  useNativeLinks();
  return null;
}
